import throttle from 'lodash/throttle';
import {
  useCallback,
  createContext,
  useContext,
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import store from 'store';

import type { BattlecardsType } from 'api/api.types';

type SetHighlightedCallback = (highlighted: boolean) => void;

type Observer = {
  node: HTMLDivElement;
  callback: SetHighlightedCallback;
  hovered?: boolean;
};

type ThrottledFunction = {
  (e: MouseEvent): void;
  cancel: () => void;
};

type BattlecardCardsHighlightingContextProps = {
  addCardObserver: (
    cardId: number,
    node: HTMLDivElement,
    callback: SetHighlightedCallback,
  ) => void;
  removeCardObserver: (cardId: number) => void;
  onBattlecard: (cardId: number) => boolean;
  getBattlecards: (cardId: number) => BattlecardsType[] | undefined;
  addBattlecardObserver: (
    battlecardId: number,
    node: HTMLDivElement,
    callback: SetHighlightedCallback,
  ) => void;
  removeBattlecardObserver: (battlecardId: number) => void;
  getBattlecardsSiblingCardIds: (cardId: number) => number[];
};

const BattlecardCardsHighlightingContext =
  createContext<BattlecardCardsHighlightingContextProps>({
    addCardObserver: (
      cardId: number,
      node: HTMLDivElement | null,
      callback: SetHighlightedCallback,
    ) => {},
    removeCardObserver: (cardId: number) => {},
    onBattlecard: (cardId: number) => false,
    getBattlecards: (cardId: number) => undefined,
    addBattlecardObserver: (
      battlecardId: number,
      node: HTMLDivElement | null,
      callback: SetHighlightedCallback,
    ) => {},
    removeBattlecardObserver: (battlecardId: number) => {},
    getBattlecardsSiblingCardIds: (cardId: number) => [],
  });

const selectCurrentBattlecards = store.select(({ battlecards }) => ({
  currentBattlecards: battlecards.currentBattlecards,
}));

const useBattlecardCardsHighlightingInternal = () => {
  const throttledMouseMoveRef = useRef<ThrottledFunction | null>(null);
  const { currentBattlecards } = useSelector(selectCurrentBattlecards);
  const getCardIdToBattlecardsMap = useCallback(
    (currentBattlecards: BattlecardsType[]) => {
      const updatedAllCardIs = new Set<number>();
      const newCardIdToBattlecardsMap = new Map<number, BattlecardsType[]>();
      currentBattlecards.forEach((battlecard) => {
        battlecard.cards?.desktop.forEach((cardId) => {
          if (!cardId) {
            return;
          }
          updatedAllCardIs.add(cardId);
          newCardIdToBattlecardsMap.set(cardId, [
            ...(newCardIdToBattlecardsMap.get(cardId) ?? []),
            battlecard,
          ]);
        });
      });

      return newCardIdToBattlecardsMap;
    },
    [],
  );

  const [cardIdToBattlecardsMap, setCardIdToBattlecardsMap] = useState<
    Map<number, BattlecardsType[]>
  >(getCardIdToBattlecardsMap(currentBattlecards));
  const cardObservers = useRef<Map<number, Observer>>(new Map());
  const battlecardObservers = useRef<Map<number, Observer>>(new Map());
  const addCardObserver = useCallback(
    (
      cardId: number,
      node: HTMLDivElement,
      callback: SetHighlightedCallback,
    ) => {
      cardObservers.current.set(cardId, { node, callback });
    },
    [],
  );
  const removeCardObserver = useCallback((cardId: number) => {
    cardObservers.current.delete(cardId);
  }, []);
  const onBattlecard = useCallback(
    (cardId: number) => {
      return cardIdToBattlecardsMap.has(cardId);
    },
    [cardIdToBattlecardsMap],
  );
  const getBattlecards = useCallback(
    (cardId: number) => {
      return cardIdToBattlecardsMap.get(cardId);
    },
    [cardIdToBattlecardsMap],
  );
  const getBattlecardsSiblingCardIds = useCallback(
    (cardId: number) => {
      const battlecards = cardIdToBattlecardsMap.get(cardId);
      if (!battlecards) {
        return [];
      }
      const siblingCards = new Set<number>();
      battlecards.forEach((battlecard) => {
        battlecard.cards?.desktop.forEach((siblingCardId) => {
          if (siblingCardId) {
            siblingCards.add(siblingCardId);
          }
        });
      });
      return Array.from(siblingCards);
    },
    [cardIdToBattlecardsMap],
  );
  const addBattlecardObserver = useCallback(
    (
      battlecardId: number,
      node: HTMLDivElement,
      callback: SetHighlightedCallback,
    ) => {
      battlecardObservers.current.set(battlecardId, { node, callback });
    },
    [],
  );
  const removeBattlecardObserver = useCallback((battlecardId: number) => {
    battlecardObservers.current.delete(battlecardId);
  }, []);

  const handleDidChangeBattlecardHoverState = useCallback(
    ({
      key,
      node,
      callback,
      hovered,
    }: {
      key: number;
      node: HTMLDivElement;
      callback: SetHighlightedCallback;
      hovered: boolean;
    }) => {
      const battlecard = currentBattlecards.find((bc) => bc.id === key);
      if (!battlecard) {
        return;
      }
      const cardIds = battlecard.cards?.desktop;
      if (!cardIds || !cardIds.length) {
        return;
      }
      // highight all cards on the battlecard
      cardIds.forEach((cardId) => {
        cardObservers.current.get(cardId)?.callback(hovered);
      });
      // save the current hover state of the battlecard to track on-vs-off for mousemove event
      battlecardObservers.current.set(key, {
        node,
        callback,
        hovered,
      });
    },
    [currentBattlecards],
  );

  const handleDidChangeCardHoverState = useCallback(
    ({
      node,
      callback,
      key,
      hovered,
    }: {
      node: HTMLDivElement;
      callback: SetHighlightedCallback;
      key: number;
      hovered: boolean;
    }) => {
      const battlecardIs = getBattlecards(key)?.map(
        (battlecard) => battlecard.id,
      );
      const siblingCardIds = getBattlecardsSiblingCardIds(key);
      // highlight all cards that are on the same battlecard
      siblingCardIds.forEach((siblingCardId) => {
        cardObservers.current.get(siblingCardId)?.callback(hovered);
      });
      // save the current hover state of the card to track on-vs-off for mousemove event
      cardObservers.current.set(key, {
        node,
        callback,
        hovered,
      });
      // set the hover state of the card
      callback(hovered);
      // highlight all the battlecards this card is on
      battlecardIs?.forEach((battlecardId) => {
        const observer = battlecardObservers.current.get(battlecardId);
        if (observer) {
          observer.callback(hovered);
        }
      });
    },
    [getBattlecardsSiblingCardIds, getBattlecards],
  );

  const getOffAndOnObservers = (
    observers: Map<number, Observer>,
    e: MouseEvent,
  ) => {
    const turnOffs: Map<number, Observer> = new Map();
    const turnOns: Map<number, Observer> = new Map();
    observers.forEach((value, key) => {
      const { node, hovered = false } = value;
      const isHovered = node.contains(e.target as Node);
      if (isHovered && !hovered) {
        turnOns.set(key, value);
      }
      if (!isHovered && hovered) {
        turnOffs.set(key, value);
      }
    });

    return {
      turnOffs,
      turnOns,
    };
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const { turnOffs: turnOffCards, turnOns: turnOnCards } =
        getOffAndOnObservers(cardObservers.current, e);
      const { turnOffs: turnOffBattlecards, turnOns: turnOnBattlecards } =
        getOffAndOnObservers(battlecardObservers.current, e);
      turnOffCards.forEach((value, key) =>
        handleDidChangeCardHoverState({ ...value, key, hovered: false }),
      );
      turnOnCards.forEach((value, key) =>
        handleDidChangeCardHoverState({ ...value, key, hovered: true }),
      );
      turnOffBattlecards.forEach((value, key) =>
        handleDidChangeBattlecardHoverState({
          ...value,
          key,
          hovered: false,
        }),
      );
      turnOnBattlecards.forEach((value, key) =>
        handleDidChangeBattlecardHoverState({
          ...value,
          key,
          hovered: true,
        }),
      );
    },
    [handleDidChangeCardHoverState, handleDidChangeBattlecardHoverState],
  );

  useEffect(() => {
    setCardIdToBattlecardsMap(getCardIdToBattlecardsMap(currentBattlecards));
  }, [getCardIdToBattlecardsMap, currentBattlecards]);

  useEffect(() => {
    throttledMouseMoveRef.current = throttle(handleMouseMove, 100);

    const handler = (e: MouseEvent) => {
      throttledMouseMoveRef.current?.(e);
    };

    document.addEventListener('mousemove', handler);
    return () => {
      document.removeEventListener('mousemove', handler);
      throttledMouseMoveRef.current?.cancel();
    };
  }, [handleMouseMove]);

  return useMemo(
    () => ({
      addCardObserver,
      removeCardObserver,
      onBattlecard,
      getBattlecards,
      addBattlecardObserver,
      removeBattlecardObserver,
      getBattlecardsSiblingCardIds,
    }),
    [
      addCardObserver,
      removeCardObserver,
      onBattlecard,
      getBattlecards,
      addBattlecardObserver,
      removeBattlecardObserver,
      getBattlecardsSiblingCardIds,
    ],
  );
};

export const BattlecardCardsHighlightingProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const value = useBattlecardCardsHighlightingInternal();

  return (
    <BattlecardCardsHighlightingContext.Provider value={value}>
      {children}
    </BattlecardCardsHighlightingContext.Provider>
  );
};

export const useBattlecardCardsHighlightingContext = () =>
  useContext(BattlecardCardsHighlightingContext);
